<script setup>
import {ref, reactive, computed} from "vue"
import ResearchEbay from "@/components/research/research-ebay.vue";
import ResearchMenu from "@/components/layout/research-menu.vue";
import Modal from "@/components/layout/modal.vue";

const mode = ref('overview')

const props = defineProps({
  value: {
    type: Object,
  },
  condition: {
    type: String,
    default: 'Used',
  },
  header: String,
  valueEditor: Object,
  withErase: Boolean,
  withNewAppraise: Boolean,
  title: String,
  embed: Boolean,
})

const sorts = ref([
  {key: 'price_asc', name: 'Lowest Price First'},
  {key: 'price_desc', name: 'Highest Price First'},
  {key: 'date_asc', name: 'Oldest Sold / Listed First'},
  {key: 'date_desc', name: 'Recently Sold / Listed First'},
])

const sortBy = ref('date_desc')

const conds = ref(['new', 'used', 'broken'])

const form = reactive({
  est_value_low: (props.valueEditor && props.valueEditor.est_value_low) || props.value?.appraisal?.value_low,
  est_value: (props.valueEditor && props.valueEditor.est_value) || props.value?.appraisal?.value
})

const matches = computed(() => {
  const {completed = [], active = [], appraisal} = props.value ?? {};
  const details = appraisal?.details ?? [];

  if (!details.length) return [];

  const listings = [...completed, ...active];

  return details.flatMap(detail => {
    const listing = listings.find(l => l.item_id === detail.item_id);
    return listing ? {...listing, ...detail} : [];
  });
});

const modes = computed(() => {
  const m = [
    {value: 'overview', text: 'Overview'},
    {value: 'local', text: 'Our Sales (' + props.value?.local?.sum['Units Sold'] + ')'},
    {value: 'sold', text: 'eBay Sold (' + props.value?.completed?.length + ')'},
    {value: 'active', text: 'eBay Active (' + props.value?.active?.length + ')'},
  ]

  if (props.value?.appraisal?.details?.length)
    m.push({value: 'matches', text: 'Matches (' + props.value.appraisal.details.length + ')'})

  return m
})

const local_fields = ref([
  {key: 'SKU', sortable: true},
  {key: 'Cond', sortable: true},
  {key: 'Model', sortable: true},
  {key: 'Price', sortable: true},
  {key: 'Channel', label: 'Chan', sortable: true},
  {key: 'Units Sold', label: "Sold", sortable: true},
  {key: 'Units Sold', label: "Sold", sortable: true},
  {key: 'Span (Days)', label: "Span", sortable: true},
  {key: 'Lead Time (Days)', label: "Lead", sortable: true},
  {key: 'Last Order (Days)', label: "Last", sortable: true},
])
const searched_q = ref((props.value && (props.value.normalized ? props.value.normalized.manufacture + ' ' + props.value.normalized.model : null)) || props.value?.query)

const appraise = reactive({
  research_q: searched_q.value || props.header,
  research_condition: (props.value?.condition || props.condition || '').toLowerCase(),
  match: false,
})
</script>

<template>
  <component :is="embed ? 'div' : Modal" :title="'Appraisal Details' + (title ? ' - ' + title : '')" height="400px"
             large>
    <modal-portal :to="embed ? 'subtop' : 'top'">
      <h6 v-if="header">{{ header }}</h6>
      <mode-selector breakpoint="md" v-if="value?.status === 'complete'" :chip="embed"
                     v-model="mode"
                     :modes="modes"/>

      <div class="d-flex my-2" v-if="mode !== 'overview' && mode !== 'local' && embed">
        <b-dropdown class="ml-auto" variant="outline-secondary" size="sm"
                    :text="'Sort By: ' + sorts.find(s => s.key == sortBy)?.name || 'UNKNOWN'">
          <b-dropdown-item v-for="sort in sorts" :key="sort.key" @click="sortBy = sort.key">{{
              sort.name
            }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </modal-portal>

    <modal-portal to="subtop" v-if="mode !== 'overview' && mode !== 'local' && !embed">
      <div class="d-flex">
        <b-dropdown class="ml-auto" variant="outline-secondary" size="sm"
                    :text="'Sort By: ' + sorts.find(s => s.key == sortBy)?.name || 'UNKNOWN'">
          <b-dropdown-item v-for="sort in sorts" :key="sort.key" @click="sortBy = sort.key">{{
              sort.name
            }}
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </modal-portal>

    <div v-if="value">
      <div v-if="value.status === 'error'">
        <b-alert show variant="danger">{{ value.error }}</b-alert>
      </div>
      <div v-else-if="value.status === 'processing'">
        <b-alert show variant="primary">Processing... Check back later for results.</b-alert>
      </div>
      <div v-else-if="mode === 'overview'">
        <b-alert show v-if="value.warnings?.length" variant="warning">
          <li v-for="w in value.warnings" :key="w">{{ w }}</li>
        </b-alert>
        <h2 class="text-center d-block" v-if="value.appraisal?.value_low">{{ value.appraisal?.value_low | money }}
          <template v-if="value.appraisal.value !== value.appraisal?.value_low">to {{
              value.appraisal?.value | money
            }}
          </template>
        </h2>
        <h6 class="text-center d-block">Confidence Score {{ value.appraisal?.confidence }}</h6>
        <div class="mt-3 mx-3">{{ value.appraisal?.reason }}</div>
        <!--<div class="mt-3 mx-3">{{ value.created | date }}</div>-->

        <div class="mt-3 mx-3" v-if="value.appraisal?.product_description">
          <h6 class="text-center text-muted">Product Info</h6>
          <div class="text-muted">{{ value.appraisal?.product_description }}</div>
        </div>
      </div>
      <div v-else-if="mode === 'local'">
        <b-table :items="value.local.data" :fields="local_fields" sort-by="Last Order (Days)" :sort-desc="false">
          <template #cell(SKU)="{value}">
            <b-link target="_blank" :href="'/item/' + value.raw">{{ value.display}}</b-link>
          </template>
          <template #cell(Price)="{value}">
            {{ value | money_short}}
          </template>
        </b-table>
      </div>
      <div v-else-if="mode === 'sold'">
        <research-ebay class="mt-2" :value="value.completed" :active="value && value.appraisal?.match" :sort="sortBy"/>
      </div>
      <div v-else-if="mode === 'active'">
        <research-ebay class="mt-2" :value="value.active" :active="value && value.appraisal?.active" :sort="sortBy"/>
      </div>
      <div v-else-if="mode === 'matches'">
        <research-ebay class="mt-2" :value="matches" :sort="sortBy"/>
      </div>
    </div>
    <b-alert show variant="secondary" class="mt-3" v-else>No appraisal available.</b-alert>

    <template #footer v-if="value?.status !== 'processing'">
      <div class="mb-3" v-if="withNewAppraise">
        <b-input-group size="sm">
          <b-input-group-prepend>
            <b-select :options="conds" v-model="appraise.research_condition"/>
          </b-input-group-prepend>
          <b-input v-model="appraise.research_q"/>
          <b-input-group-append>
            <b-btn @click="$root.$emit('submit', {appraise})" variant="outline-primary">Appraise</b-btn>
          </b-input-group-append>
        </b-input-group>
      </div>

      <div class="mb-3" v-if="value && valueEditor && value.status === 'complete'">
        <b-input-group size="sm" prepend="Value $">
          <b-input-group-prepend>
            <b-input @keydown.enter.prevent="$refs.value_high.focus()" v-model="form.est_value_low" autofocus/>
          </b-input-group-prepend>
          <b-input-group-prepend is-text> to $</b-input-group-prepend>
          <b-input v-model="form.est_value" @keydown.enter.prevent="$root.$emit('submit', {valueEdit: form})"
                   ref="value_high"/>
          <b-input-group-append>
            <b-btn size="sm" variant="outline-primary" @click="$root.$emit('submit', {valueEdit: form})">Save</b-btn>
          </b-input-group-append>
        </b-input-group>
      </div>

      <research-menu class="mr-2" large :value="searched_q || header"/>
      <b-btn size="sm" class="mr-2" v-if="value && withErase" @click="$root.$emit('submit', {erase: true})"
             variant="outline-danger">Erase
      </b-btn>
      <b-btn size="sm" @click="$root.$emit('submit')" variant="outline-secondary">Close</b-btn>
    </template>
  </component>
</template>

<style scoped>

</style>